import { FC } from 'react';
import {
  ChevronLeftRounded,
  ChevronRightRounded,
  PersonAddAltRounded,
  TodayRounded,
} from '@mui/icons-material';
import { Button, CircularProgress, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import './TeamDayToolbar.scss';

interface TeamDayToolbarProps {
  dateLabel: string;
  isFetching: boolean;
  onPrev: () => void;
  onNext: () => void;
  onResetToday: () => void;
  onOpenInviteModal: () => void;
}

const TeamDayToolbar: FC<TeamDayToolbarProps> = ({
  dateLabel,
  isFetching,
  onPrev,
  onNext,
  onResetToday,
  onOpenInviteModal,
}) => {
  const { t } = useTranslation();

  return (
    <header className="team-day-toolbar">
      <div className="team-day-toolbar__copy">
        <Typography variant="h2" component="h1" className="team-day-toolbar__title">
          {t('team.day.title')}
        </Typography>
        <Typography variant="body2" component="p" className="team-day-toolbar__subtitle">
          {t('team.day.subtitle', { date: dateLabel })}
        </Typography>
      </div>
      <div className="team-day-toolbar__actions">
        {isFetching && (
          <div className="team-day-toolbar__updating-block" aria-live="polite">
            <CircularProgress size={14} className="team-day-toolbar__updating-spinner" />
            <Typography
              variant="subtitle2"
              component="span"
              className="team-day-toolbar__updating-label"
            >
              {t('team.common.updating')}
            </Typography>
          </div>
        )}
        <Button
          type="button"
          variant="outlined"
          className="team-page__button team-page__button--secondary team-day-toolbar__today-button"
          startIcon={<TodayRounded />}
          onClick={onResetToday}
        >
          <Typography variant="body2" component="span">
            {t('team.common.today')}
          </Typography>
        </Button>
        <div className="team-day-toolbar__date-switcher" aria-label={t('team.day.dateAria')}>
          <IconButton
            type="button"
            className="team-day-toolbar__date-button"
            aria-label={t('team.day.previousDay')}
            onClick={onPrev}
          >
            <ChevronLeftRounded />
          </IconButton>
          <Typography variant="body2" component="span" className="team-day-toolbar__date-label">
            {dateLabel}
          </Typography>
          <IconButton
            type="button"
            className="team-day-toolbar__date-button"
            aria-label={t('team.day.nextDay')}
            onClick={onNext}
          >
            <ChevronRightRounded />
          </IconButton>
        </div>
        <Button
          type="button"
          variant="contained"
          color="primary"
          className="team-page__button team-page__button--primary team-day-toolbar__invite-button"
          startIcon={<PersonAddAltRounded />}
          onClick={onOpenInviteModal}
        >
          <Typography variant="body2" component="span">
            {t('team.common.invite')}
          </Typography>
        </Button>
      </div>
    </header>
  );
};

export default TeamDayToolbar;
